import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { token2022ProgramId, tokenProgramId } from '../constants'
import type { ClientCallParameters } from './create'

const tokenAccountSchema = z.object({
  account: z.object({
    /** Token state data associated with the account, either as encoded binary data or in JSON format */
    data: z.object({
      parsed: z.object({
        info: z.object({
          isNative: z.boolean(),
          mint: z.string(),
          owner: z.string(),
          state: z.string(),
          tokenAmount: z.object({
            amount: z.string(),
            decimals: z.number(),
            uiAmount: z.number(),
            uiAmountString: z.string(),
          }),
        }),
        type: z.string(),
      }),
      program: z.string(),
      space: z.number().int(),
    }),
    /** boolean indicating if the account contains a program (and is strictly read-only) */
    executable: z.boolean(),
    /** number of lamports assigned to this account, as a u64 */
    lamports: z.number().int(),
    /** base-58 encoded Pubkey of the program this account has been assigned to */
    owner: z.string(),
    /** the epoch at which this account will next owe rent, as u64 */
    rentEpoch: z.number().int(),
    /** the data size of the account */
    space: z.number().int(),
  }),
  /** the account Pubkey as base-58 encoded string */
  pubkey: z.string(),
})

export type TokenAccount = z.infer<typeof tokenAccountSchema>

const schema = z.object({
  jsonrpc: z.literal('2.0'),
  result: z.object({
    context: z.object({
      apiVersion: z.string(),
      slot: z.number().int(),
    }),
    value: z.array(tokenAccountSchema),
  }),
  id: z.number(),
})

type GetTokenAccountsByOwnerParameters = ClientCallParameters & {
  ownerPubKey: string
}

// TODO
// token program が TokenProgram , Token2022Program の 2 つ以外に追加されたら更新する

export const getTokenAccountsByOwner = async ({
  endpoint,
  headers,
  ownerPubKey,
}: GetTokenAccountsByOwnerParameters): Promise<readonly TokenAccount[]> => {
  const tokenProgramResponse = await rest(endpoint, {
    fetchOptions: {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'getTokenAccountsByOwner',
        params: [
          ownerPubKey,
          {
            programId: tokenProgramId,
          },
          {
            encoding: 'jsonParsed',
          },
        ],
      }),
    },
    schema: schema,
  })
  const token2022ProgramResponse = await rest(endpoint, {
    fetchOptions: {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'getTokenAccountsByOwner',
        params: [
          ownerPubKey,
          {
            programId: token2022ProgramId,
          },
          {
            encoding: 'jsonParsed',
          },
        ],
      }),
    },
    schema: schema,
  })
  return [
    ...tokenProgramResponse.result.value,
    ...token2022ProgramResponse.result.value,
  ]
}
